<script setup lang="ts">import { computed as _computed } from 'vue';

import _ from "underscore";
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  SwitchRoot,
  SwitchThumb,
} from "reka-ui";
import { VList } from "virtua/vue";
import debounce from "lodash.debounce";
import { computed, ref, watch } from "vue";

import {
  getMarkStatus,
  isEqual,
  isHighlightAnnotation,
  isItemBurned,
  isItemBurning,
  isPasswordProtected,
  isRectangleAnnotation,
  isTimeIndexRedaction,
  isTranscriptAnnotation,
} from "@/services/UtilitiesService";
import { isRedaction } from "@/services/AnnotationService";

import { Metadata } from "@/models/Metadata";

import RsIcon from "@/components/Icon.vue";
import { RsRecordSearch } from "@/components";
import RsSortBy from "@/components/SortBy.vue";
import TagTooltip from "@/components/Tooltip/TagTooltip.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import FileListTooltip from "@/components/Tooltip/FileListTooltip.vue";
import ViewingPackageTooltip from "@/components/Tooltip/ViewingPackageTooltip.vue";

import { useIoaStore } from "@/stores/ioa-store";
import { useBrandStore } from "@/stores/brand-store";
import { useFiltersStore } from "@/stores/filters-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { ModuleStatus } from "@/models/ModuleStatus";

import type { Tag } from "@/models/Tag";
import type EvidenceCount from "@/models/RecordEvidenceCount";
import type { BrandViewerConfig } from "@/models/BrandViewerConfig";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineEmits<DockviewPanelComponentEmits>();
defineProps<DockviewPanelComponentProps<false>>();

const filesRef = ref<HTMLDivElement | null>(null);
const virtualScrollerRef = ref<InstanceType<typeof VList>>();

const routeParams = useCaseRouteParams();

const tree = computed(() => {
  const { filteredFilesInCurrentFolder: files, folders } = useMetadataStore();
  const result: Array<
    { type: "file"; item: (typeof files)[number] } | { type: "folder"; item: (typeof folders)[number] }
  > = [];

  if (groupByFolders.value) {
    result.push(...folders.map((folder) => ({ type: "folder" as const, item: folder })));
  }

  result.push(...files.map((file) => ({ type: "file" as const, item: file })));

  return result;
});
type TreeItem = (typeof tree.value)[number];

const hasNoFiles = _computed(() => useMetadataStore().files.length === 0);

const failedFiles = computed(() => useMetadataStore().failedFiles);

const isErrored = computed(() => useMetadataStore().moduleStatus === ModuleStatus.Error);

const currentItem = _computed(() => {
  return useCaseFileStore().currentFile;
});

const path = _computed(() => {
  return (fileReference: string) => {
    const cache = useCaseFileStore().cachedFiles.find((c) => c.fileReference === fileReference);

    const { brand, casename } = routeParams.value;
    return cache ? cache.path : `/${brand}/${casename}/${fileReference}`;
  };
});

const currentFolder = _computed(() => {
  return useFiltersStore().filter.currentFolder;
});

const groupByFolders = computed({
  get() {
    return useFiltersStore().filter.groupByFolders;
  },
  set(isGroupByFolders) {
    useFiltersStore().changeGroupByFolders({ groupByFolders: isGroupByFolders });
  },
});

const mappedInstances = _computed(() => {
  return useIoaStore().mappedInstances;
});

const annotationDictionary = _computed(() => {
  return useAnnotationStore().annotationDictionary;
});

const summariserEnabled = _computed(() => {
  return useBrandStore().viewerConfig.recordList?.summariser?.enabled ?? false;
});

const ioaDisabled = _computed(() => {
  return useBrandStore().viewerConfig.recordList?.ioa?.disabled;
});

watch(
  [() => routeParams.value.casefile, tree, virtualScrollerRef],
  debounce(() => {
    if (!routeParams.value.casefile || tree.value.length < 1) return;

    const index = tree.value.findIndex((f) => f.type === "file" && f.item.fileReference === routeParams.value.casefile);
    virtualScrollerRef.value?.scrollToIndex(index, { smooth: true, align: "nearest" });
  }, 0),
  { immediate: true, flush: "post" },
);

function selectFolder(folder: string) {
  useFiltersStore().changeCurrentFolder({ currentFolder: folder });
}

function showTagLine(item: Metadata) {
  return (
    (item.tags ?? []).length > 0 ||
    (item.evidenceCounts ?? []).length > 0 ||
    Boolean(mappedInstances.value[item.fileReference]) ||
    (summariserEnabled.value && Boolean(item.edxSummariserStatus)) ||
    hasTimeIndexRedaction(item) ||
    hasTranscriptDocumentRedaction(item) ||
    isItemBurned(item) ||
    isItemBurning(item) ||
    (annotationDictionary.value[item.fileReference]?.length ?? 0) > 0
  );
}

function groupAutoTags(tags: Tag[]) {
  // We need to knock out any tags that shouldn't be shown according to the brand config
  const autoTags = useBrandStore().viewerConfig.recordList?.autoTags;
  return groupTags(tags, autoTags ?? []);
}

function groupUserTags(tags: Tag[]) {
  // We need to knock out any tags that shouldn't be shown according to the brand config
  const userTags = useBrandStore().viewerConfig.recordList?.userTags;
  return groupTags(tags, userTags ?? []);
}

function groupTags(tags: Tag[], tagsConfig: NonNullable<NonNullable<BrandViewerConfig>["recordList"]>["userTags"]) {
  return _.chain(tags)
    .map((tag) => {
      const config = tagsConfig.find((f) => isEqual(f.category, tag.tagType)) || ({} as never);
      return {
        ...tag,
        tagType: config.icon || tag.tagType,
        config,
      };
    })
    .filter((t) => t.config.display !== "none")
    .groupBy((tag) => tag.tagType)
    .value();
}

function getCountsWithConfig(evidenceCounts: EvidenceCount[] = []) {
  const configCounts = useBrandStore().viewerConfig.recordList?.evidenceCounts ?? [];
  const defaultConfig = {
    display: "show",
    showValue: true,
    showName: true,
    icon: undefined,
  };

  return evidenceCounts
    .map((ec) => {
      const config = configCounts.find((f) => isEqual(f.name, ec.name)) ?? defaultConfig;
      return { ...ec, config };
    })
    .filter((f) => f.config.display !== "none");
}

function getAnalysisStatus(item: Metadata) {
  if (!item.edxProcessingStatus || item.edxProcessingStatus === "analysed") {
    return "analysed";
  }

  switch (item.edxProcessingStatus) {
    case "justUploaded":
      return "uploaded";
    case "readyToView":
      return "ready";
    case "issue":
      return "error";
  }
}

function isDupeSelection(item: Metadata): string {
  if (!routeParams.value.casefile) {
    return "";
  }

  // We have selected the master copy of which this is a dupe
  if (routeParams.value.casefile === item.original) {
    return "font-weight-bold";
  }

  // Find the curently selected item
  const activeItem: Metadata | undefined = currentItem.value;

  // Our selected item is not a dupe
  if (!activeItem?.original) {
    return "";
  }

  return item.fileReference === activeItem.original || item.original === activeItem.original ? "font-weight-bold" : "";
}

function itemClasses(item: Metadata): string[] {
  return [
    "file-item",
    isSameItem(item) ? "item-active" : "",
    getAnalysisStatus(item),
    isPasswordProtected(item) ? "protected" : "",
    isDupeSelection(item),
  ];
}

// Use this to create a functional icon component to speed up rendering
function getIconClasses(item: Metadata): string {
  switch (item.fileType) {
    case "unknown":
      return ["far", "fa-question-circle"].join(" ");
    case "email":
      return ["far", "fa-envelope"].join(" ");
    case "text":
      return ["far", "fa-file-alt"].join(" ");
    case "image":
      return ["far", "fa-image"].join(" ");
    case "zip":
      return ["far", "fa-file-archive"].join(" ");
    case "audio":
      return ["fas", "fa-music"].join(" ");
    case "publisher":
      return ["far", "fa-file-video"].join(" ");
    case "html":
      return ["fab", "fa-html5"].join(" ");
    case "xps":
      return ["far", "fa-file-pdf"].join(" ");
    case "visio":
      return ["fas", "fa-project-diagram"].join(" ");
    case undefined:
      return ["fas", "fa-file-circle-question"].join(" ");
    default:
      return ["far", `fa-file-${item.fileType}`].join(" ");
  }
}

function hasTimeIndexRedaction(item: Metadata): boolean {
  return (
    annotationDictionary.value[item.original]?.find(
      (f) => f.parsedMarkData != null && isRedaction(f.parsedMarkData) && isTimeIndexRedaction(f.parsedMarkData),
    ) != null
  );
}

function hasTranscriptDocumentRedaction(item: Metadata): boolean {
  return (
    annotationDictionary.value[item.original]?.find(
      (f) =>
        f.parsedMarkData != null &&
        isRedaction(f.parsedMarkData) &&
        (isTranscriptAnnotation(f.parsedMarkData) ||
          isRectangleAnnotation(f.parsedMarkData) ||
          isHighlightAnnotation(f.parsedMarkData)),
    ) != null
  );
}

function isSameItem(metadata: Metadata): boolean {
  return currentItem.value?.fileReference === metadata.fileReference;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getSummariserIcon(item: Metadata) {
  switch ((item.edxSummariserStatus || "").toLowerCase()) {
    case "pending":
      return "fas fa-clock";
    case "issue":
      return "fas fa-exclamation";
    case "not applicable":
      return "fas fa-ban";
    default:
      return "tw-hidden";
  }
}

function getPreConvertIcon(item: Metadata) {
  switch ((item.edxPreConvertStatus || "").toLowerCase()) {
    case "pending":
      return "fas fa-clock";
    case "issue":
      return "fas fa-exclamation";
    case "success":
      return "fas fa-check";
    default:
      return "tw-hidden";
  }
}

function getUserOrAcceptedMarks(item: Metadata): number {
  const marks = useAnnotationStore().annotationDictionary[item.fileReference] ?? [];
  return marks
    ?.map((a) => ({ ...a, status: getMarkStatus(a) }))
    .filter((m) => /user/i.test(m.status) || /accepted/i.test(m.status))
    .filter((m) => !/"?type"?\s?:\s?"\w*?Redaction"?/i.test(m.markData)).length;
}

function getOutstandingMarks(item: Metadata): number {
  const marks = useAnnotationStore().annotationDictionary[item.fileReference] || [];
  return marks.map((a) => ({ ...a, status: getMarkStatus(a) })).filter((m) => /candidate/i.test(m.status)).length;
}
</script>
<template>
  <div ref="filesRef" class="file-list tw-size-full tw-overflow-y-auto">
    <StringTooltip
      v-if="failedFiles.length > 0"
      item="The files couldn't be displayed due to a parsing issue. Please report this problem."
    >
      <p class="tw-sticky tw-top-0 tw-flex tw-items-center tw-justify-center tw-gap-2">
        <i class="fas fa-triangle-exclamation" />{{ failedFiles.length }} unique
        {{ failedFiles.length === 1 ? "file is" : "files are" }} hidden. Please report.
      </p>
    </StringTooltip>
    <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1 tw-bg-gray-800 tw-p-1 [&>*]:tw-mx-0">
      <RsSortBy />
      <div
        v-if="useBrandStore().viewerConfig.recordList?.foldersViewEnabled"
        class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-p-1"
      >
        <label for="foldersView" class="tw-min-w-max tw-cursor-pointer tw-font-medium tw-text-white">
          Folders View
        </label>
        <SwitchRoot
          id="foldersView"
          v-model="groupByFolders"
          class="tw-relative tw-flex tw-h-4 tw-w-7 tw-cursor-pointer tw-items-center tw-rounded-full tw-border-[0.5px] tw-border-white tw-bg-purple-100 tw-p-0.5 tw-shadow-sm data-[state=checked]:tw-bg-white"
        >
          <SwitchThumb
            class="will-change-transform tw-relative tw-left-0 tw-block tw-aspect-square tw-w-1/2 tw-rounded-full tw-bg-purple-900 tw-shadow-sm tw-transition-[left] tw-duration-100 data-[state=checked]:tw-left-1/2 data-[state=checked]:tw-bg-green-700"
          />
        </SwitchRoot>
      </div>
      <RsRecordSearch
        v-if="useBrandStore().viewerConfig.recordList?.showSearch"
        :brand="routeParams.brand"
        :case-reference="routeParams.casename"
        class="tw-flex-no-wrap tw-m-1 tw-flex tw-flex-1"
      />
    </div>
    <div id="files" class="files tw-flex tw-h-full tw-flex-col tw-gap-0.5 tw-overflow-auto">
      <h2 v-if="hasNoFiles" class="tw-mt-3 tw-text-center">
        <span>There are no files for this case</span>
      </h2>
      <template v-else>
        <div
          v-if="currentFolder && groupByFolders"
          class="file-item tw-py-0.5 tw-pl-0.5"
          @click="selectFolder(currentFolder.split('/').slice(0, -1).join('/'))"
        >
          <div class="tw-truncate" title="Back">
            <i class="fa fa-arrow-left"></i>
            {{ currentFolder.split("/").pop() }}
          </div>
        </div>
        <VList
          ref="virtualScrollerRef"
          v-slot="{ item: { type, item } }: { index: number; item: TreeItem }"
          class="tw-shrink-0 tw-grow tw-divide-y"
          :data="tree"
          :item-size="38"
        >
          <div
            v-if="type === 'folder'"
            :key="item"
            class="file-item tw-px-2 tw-py-0.5"
            @click="selectFolder(currentFolder ? currentFolder + '/' + item : item)"
          >
            <div class="tw-truncate" :title="item">
              <i class="fas fa-folder"></i>
              {{ item }}
            </div>
          </div>
          <router-link
            v-else-if="type === 'file'"
            :id="`id-${item.fileReference}`"
            :key="item.fileReference"
            :to="path(item.original)"
            :class="itemClasses(item)"
            :aria-label="`View ${item.displayName} file`"
            class="tw-grid tw-grid-rows-[repeat(2,max-content)] tw-gap-y-0.5 tw-px-2 tw-py-0.5 [&>*]:tw-min-w-0"
          >
            <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-x-2 tw-gap-y-1">
              <FileListTooltip side="bottom" align="start" :item="item">
                <div class="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-truncate">
                  <RsIcon :icon="getIconClasses(item)" :has-parent="!!item.parent && item.fileType != null" />
                  <i v-if="String(item.isDraft).toLowerCase() === 'true'" class="far fa-save"></i>
                  <i v-if="String(item.isDuplicate).toLowerCase() === 'true'" class="far fa-copy"></i>
                  <i v-if="isPasswordProtected(item)" class="fa fa-unlock-alt"></i>
                  <span class="tw-truncate tw-break-words">
                    {{ item.displayName || "[UNKNOWN_FILE_NAME]" }}
                  </span>
                </div>
              </FileListTooltip>
              <div class="tw-flex-1"></div>
              <div class="file-item-metadata tw-flex tw-gap-2">
                <StringTooltip v-if="item.edxPageCount" side="top" align="start" :item="`${item.edxPageCount} pages`">
                  <span
                    class="file-item-metadata-content tw-truncate tw-rounded-sm tw-bg-[#dae1e7] tw-px-1 tw-py-0.5 tw-text-xs"
                  >
                    {{ item.edxPageCount }}
                  </span>
                </StringTooltip>
                <StringTooltip v-if="item.aiClassification" side="top" align="start" :item="item.aiClassification">
                  <span class="file-item-metadata-content tw-truncate tw-rounded-sm tw-bg-[#dae1e7] tw-px-1 tw-py-0.5">
                    {{ item.aiClassification }}
                  </span>
                </StringTooltip>
                <StringTooltip v-if="item.notes" :item="`Notes: ${item.notes}`">
                  <span class="file-item-metadata-content tw-truncate tw-rounded-sm tw-bg-[#dae1e7] tw-px-1 tw-py-0.5">
                    <i class="far fa-sticky-note"></i>
                    {{ item.notes }}
                  </span>
                </StringTooltip>
                <span
                  v-if="item.edxIssueDate"
                  class="file-item-metadata-content tw-truncate tw-rounded-sm tw-bg-[#dae1e7] tw-px-1 tw-py-0.5"
                >
                  {{ item.edxIssueDate }}
                </span>
              </div>
              <div class="file-item-icons tw-flex tw-gap-2 empty:tw-hidden">
                <ViewingPackageTooltip v-if="item.edxPreConvertStatus" side="top" align="end" :item="item">
                  <div :class="/issue/i.test(item.edxPreConvertStatus) && 'tw-text-red-600'" class="tw-flex-grow">
                    <i class="far fa-images tw-ml-1" />
                    <i :class="getPreConvertIcon(item)" />
                  </div>
                </ViewingPackageTooltip>
                <template v-for="marksCount in [getUserOrAcceptedMarks(item)]" :key="marksCount">
                  <StringTooltip
                    v-if="marksCount > 0"
                    side="top"
                    align="start"
                    :item="`${marksCount} ${marksCount === 1 ? 'mark' : 'marks'}`"
                  >
                    <span
                      class="file-item-metadata-content tw-truncate tw-rounded-sm tw-bg-[#ffff00] tw-px-1 tw-py-0.5 tw-text-xs tw-text-black tw-opacity-75"
                    >
                      {{ marksCount }}
                    </span>
                  </StringTooltip>
                </template>
                <template v-for="outstandingMarks in [getOutstandingMarks(item)]" :key="outstandingMarks">
                  <StringTooltip
                    v-if="summariserEnabled && outstandingMarks"
                    side="top"
                    align="start"
                    :item="`${outstandingMarks} Candidate Bookmarks`"
                  >
                    <span
                      class="file-item-metadata-content tw-truncate tw-rounded-sm tw-bg-orange-300 tw-px-1 tw-py-0.5 tw-text-xs tw-text-black tw-opacity-75"
                    >
                      {{ outstandingMarks }}
                    </span>
                  </StringTooltip>
                </template>
                <div
                  v-for="({ awaitingTranscriptDocument, awaitingTimeIndex, burning, burned }, idx) in [
                    {
                      burned: isItemBurned(item),
                      burning: isItemBurning(item),
                      awaitingTimeIndex: hasTimeIndexRedaction(item),
                      awaitingTranscriptDocument: hasTranscriptDocumentRedaction(item),
                    },
                  ]"
                  :key="idx"
                  class="tw-flex tw-flex-grow tw-items-center empty:tw-hidden"
                >
                  <StringTooltip
                    v-if="burning"
                    side="top"
                    align="end"
                    item="This file is currently having some redactions committed. It will be available to view once that process is complete."
                  >
                    <i class="fas fa-eraser tw-text-red-600"></i>
                  </StringTooltip>
                  <StringTooltip v-else-if="burned" side="top" align="end" item="This file has committed redactions.">
                    <i class="fas fa-poll-h"></i>
                  </StringTooltip>
                  <StringTooltip
                    v-if="awaitingTranscriptDocument"
                    side="top"
                    align="end"
                    item="This file has uncommitted redactions."
                  >
                    <i class="fas fa-eraser tw-text-purple-400" />
                  </StringTooltip>
                  <StringTooltip
                    v-if="awaitingTimeIndex"
                    side="top"
                    align="end"
                    item="This file has uncommitted time-based redactions."
                  >
                    <div
                      class="tw-relative tw-z-0 tw-grid tw-grid-cols-1 tw-grid-rows-1 tw-place-items-center tw-text-purple-400 *:tw-col-span-full *:tw-row-span-full"
                    >
                      <i class="fas fa-music fa-2xs" />
                      <i class="fas fa-ban fa-lg"></i>
                    </div>
                  </StringTooltip>
                </div>
              </div>
            </div>
            <div
              v-if="showTagLine(item)"
              class="file-item-tags tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-x-2 tw-gap-y-1 empty:tw-hidden"
            >
              <StringTooltip
                v-for="(tags, tagType) in groupAutoTags(
                  (item.autoTags ?? []) as Array<NonNullable<typeof item.autoTags>[number]>,
                )"
                :key="`${item.fileReference}-${tagType}`"
                side="top"
                align="start"
                :item="`${tagType}: ${tags.map((t) => t.name).join(', ')}`"
              >
                <span
                  class="tw-max-w-72 tw-truncate tw-rounded-sm tw-bg-[#6c757d] tw-px-1 tw-py-0.5 tw-text-white"
                  :class="tags.map((t) => `${t.classSafeTagType}-${t.classSafeName}`)"
                >
                  <template v-if="!tags[0].config.hideCategory">{{ tagType }}</template>
                  <template v-if="!tags[0].config.hideCategory && !tags[0].config.hideValue">: </template>
                  <template v-if="!tags[0].config.hideValue">{{ tags.map((t) => t.name).join(", ") }}</template>
                </span>
              </StringTooltip>
              <StringTooltip
                v-for="count in getCountsWithConfig(item.evidenceCounts)"
                :key="`${item.fileReference}-${count.name}`"
                :item="count.name"
                side="top"
                align="start"
              >
                <span
                  class="file-item-tags-tag tw-max-w-72 tw-truncate tw-rounded-sm tw-bg-[#6c757d] tw-px-1 tw-py-0.5 tw-text-white"
                  :class="`${count.name.replace(/\s/g, '').toLowerCase()}`"
                >
                  <span v-show="count.config.display !== 'hide'">
                    {{ count.config.showName ? count.name : count.config.icon
                    }}<template v-if="count.config.showValue">: {{ count.value }}</template>
                  </span>
                </span>
              </StringTooltip>
              <StringTooltip
                v-for="(tags, tagType) in groupUserTags(item.tags as Array<NonNullable<typeof item.tags>[number]>)"
                :key="`${item.fileReference}-${tagType}`"
                side="top"
                align="start"
                :item="`${tagType}: ${tags.map((t) => t.name).join(', ')}`"
              >
                <span
                  class="file-item-tags-tag tw-max-w-72 tw-truncate tw-rounded-sm tw-bg-[#6c757d] tw-px-1 tw-py-0.5 tw-text-white"
                  :class="`${(tags[0] || {}).classSafeTagType}`"
                >
                  <template v-if="!tags[0].config.hideCategory">{{ tagType }}</template>
                  <template v-if="!tags[0].config.hideCategory && !tags[0].config.hideValue">: </template>
                  <template v-if="!tags[0].config.hideValue">{{ tags.map((t) => t.name).join(", ") }}</template>
                </span>
              </StringTooltip>
              <TagTooltip
                v-if="mappedInstances[item.fileReference] && !ioaDisabled"
                side="top"
                :item="mappedInstances[item.fileReference].tags"
              >
                <span
                  :class="
                    mappedInstances[item.fileReference].tags.map((t) => `${t.classSafeTagType}-${t.classSafeName}`)
                  "
                  class="instance tw-max-w-72 tw-truncate tw-rounded-sm tw-bg-[#6c757d] tw-px-1 tw-py-0.5 tw-text-white"
                >
                  #{{ (mappedInstances[item.fileReference].index ?? 0) + 1 }}
                </span>
              </TagTooltip>
            </div>
          </router-link>
        </VList>
      </template>
    </div>
  </div>
  <DialogRoot v-if="isErrored" open>
    <DialogPortal>
      <DialogOverlay class="tw-fixed tw-inset-0 tw-z-[1000000] tw-h-full tw-w-full tw-bg-black/30" />
      <DialogContent
        :aria-describedby="undefined"
        class="tw-fixed tw-inset-0 tw-z-[1000000] tw-flex tw-items-center tw-justify-center *:tw-pointer-events-auto"
        style="pointer-events: none"
      >
        <div
          class="tw-w-full tw-max-w-md tw-space-y-2 tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-4 tw-shadow-lg"
        >
          <DialogTitle class="tw-text-balance tw-text-center tw-text-lg tw-font-semibold">
            Unable to load Case Files
          </DialogTitle>
          <DialogDescription class="tw-flex tw-items-center tw-gap-2 tw-text-pretty tw-rounded-md tw-text-center">
            An error occurred while requesting the files for case ({{ routeParams.casename }}). Please try reloading the
            page. If the issue persists, kindly report it to your support team.
          </DialogDescription>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
<style lang="scss">
.scroller {
  height: 100%;
}
.v-context {
  ul {
    li {
      &:hover {
        background: #f1f5f8 !important;
        color: #000 !important;
      }
    }
  }
}

a:hover {
  color: inherit;
}

.file-list {
  display: flex;
  flex-direction: column;

  .files {
    .split-vertical {
      overflow-x: auto;
    }
  }

  .file-item {
    cursor: pointer;

    &.item-active {
      background: #ddd6e8;
      .file-item-metadata {
        background: #ddd6e8;
      }
    }

    &:hover {
      background: #f3ebff;
      .file-item-metadata {
        background: #f3ebff;
      }
    }

    &.uploaded {
      color: #999900;
    }
    &.ready {
      color: #007bff;
    }
    &.error {
      color: #cc0000;
    }
    &.protected {
      color: #cc0000;
    }

    .file-item-tags {
      .file-item-tags-tag {
        &.bolder {
          background-color: #b8c2cc;
        }
      }
    }
    .file-item-metadata {
      .file-item-metadata-content {
        &.bolder {
          background-color: #b8c2cc;
        }
      }
    }
  }
}
</style>
